/**
 * Reference tunings — where "A" sits (Gordon 2026-10-31: the tuner in the
 * Studio, and coπ "set tuner to 432 or 440 or whatever … any of the classic
 * frequencies … or exact, like 441 Hz"). Names live in messages (tuner.presets.*).
 * `words` are how people say them — English, Spanish, French, accents stripped.
 */

export interface TuningPreset { id: string; a4: number; words: string[] }

export const TUNINGS: TuningPreset[] = [
  { id: 'iso440', a4: 440, words: ['standard', 'iso', 'concert', 'normal', 'modern', 'estandar', 'concierto', 'moderna', 'moderne'] },
  { id: 'verdi432', a4: 432, words: ['verdi', 'natural', 'healing', 'naturelle'] },
  { id: 'orch442', a4: 442, words: ['orchestra', 'orchestral', 'european', 'orquesta', 'orquestal', 'europea', 'orchestre', 'europeenne'] },
  { id: 'orch443', a4: 443, words: ['berlin', 'vienna'] },
  { id: 'french435', a4: 435, words: ['french', 'diapason', 'france', 'francesa', 'frances', 'francais', 'francaise'] },
  { id: 'scientific', a4: 430.54, words: ['scientific', 'philosophical', 'sauveur', 'c 256', 'c256', 'cientifica', 'cientifico', 'filosofica', 'scientifique', 'philosophique'] },
  { id: 'classical430', a4: 430, words: ['classical', 'mozart', 'beethoven', 'clasica', 'clasico', 'classique'] },
  { id: 'baroque415', a4: 415, words: ['baroque', 'bach', 'barroca', 'barroco'] },
  { id: 'chorton466', a4: 466, words: ['chorton', 'choir', 'organ', 'high baroque', 'coro', 'organo', 'choeur', 'orgue'] },
];

export const A4_MIN = 380;
export const A4_MAX = 500;

/** The classic tuning nearest to a frequency. */
export function nearestTuning(a4: number): TuningPreset {
  return TUNINGS.reduce((best, t) => (Math.abs(t.a4 - a4) < Math.abs(best.a4 - a4) ? t : best), TUNINGS[0]);
}

/** A tuning named in speech ("baroque", "verdi tuning"), if any. */
export function tuningByWords(text: string): TuningPreset | undefined {
  const t = ` ${text.toLowerCase()} `;
  return TUNINGS.find(p => p.words.some(w => t.includes(` ${w} `) || t.includes(` ${w}s `)));
}

/** "441", "441.5", "441,5" → 441.5 (only plausible A4 values). */
export function parseHz(text: string): number | null {
  const m = /(\d{3}(?:[.,]\d{1,2})?)/.exec(text);
  if (!m) return null;
  const hz = parseFloat(m[1].replace(',', '.'));
  return hz >= 300 && hz <= 600 ? hz : null;
}
